import { BulletList } from '@tiptap/extension-list';
import type { CommandProps } from '@tiptap/core';
import type { ResolvedPos } from '@tiptap/pm/model';

import type { TUlPattern } from './listPatterns';
import { getRootList, patchPatternTree } from './listUtils';
import getListPatternPlugin from '../plugins/getListPatternPlugin';

// Using a separate group key to avoid type conflicts with the existing 'bulletList' commands group.
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    bulletListWithStyle: {
      setBulletListPattern: (pattern: TUlPattern | null) => ReturnType;
    };
  }
}

/**
 * Extends the standard BulletList with a `listPattern` attribute.
 * Keeps the name `bulletList` so existing commands and isActive checks work.
 */
const BulletListWithStyle = BulletList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      listPattern: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-list-pattern') || null,
        renderHTML: (attrs) => (attrs.listPattern ? { 'data-list-pattern': attrs.listPattern } : {}),
      },
    };
  },

  addCommands() {
    const listTypeName = this.name;
    const parent = this.parent?.() ?? {};

    return {
      ...parent,

      setBulletListPattern:
        (pattern: TUlPattern | null) =>
        ({ state, tr }: CommandProps) => {
          const root = getRootList(state.selection.$from as ResolvedPos, listTypeName);
          if (!root) return false;

          const ulType = state.schema.nodes[listTypeName];
          patchPatternTree(tr, root.pos, root.node, ulType, pattern);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [getListPatternPlugin(this.name, 'ul-pattern-normalize')];
  },
});

export default BulletListWithStyle;
